// Il menù contestuale dell'editor, e le righe che si fanno notare.
//
// Due idee, una sola ragione. Le direttive `<? META:… ?>` non sono prosa: sono
// configurazione dentro il testo, e il documento cambia per colpa loro. Quindi
// si VEDONO (la riga si illumina) ma non si intromettono: il comando per
// cambiarle sta dove si cercano i comandi, cioè nel tasto destro.
//
// La prima stesura metteva un bottone sempre visibile in fondo alla riga.
// Funzionava, ma stava lì anche quando non serviva — e in un editor ogni cosa
// che sta lì sempre è una cosa che si legge sempre.

import { StateEffect, StateField, type Extension, type Range } from "@codemirror/state";
import { MARCATURE, marca, marcatureIn, marcaturaDellaSelezione, togli } from "./markup";
import {
    Decoration,
    EditorView,
    ViewPlugin,
    showTooltip,
    type DecorationSet,
    type Tooltip,
    type ViewUpdate,
} from "@codemirror/view";


// `<? META:chiave "valore" ?>` oppure col valore nudo. I gruppi servono a
// trovare il valore per POSIZIONE: si riscrive quello, non la riga.
const META = /<\?\s*META:(\w+)\s+(?:"([^"]*)"|([^\s?][^?]*?))\s*\?>/g;

const FENCE = /^\s*(`{3,}|~{3,})/;


export interface AzioneEsterna
{
    /** Cosa fare. */
    fai: () => void;
    /** Se c'è, la voce è spenta e questo dice perché. */
    impedita?: () => string | undefined;
}


export interface Opzioni
{
    /** Che cosa fa la voce «Componi» del menù. */
    componi?: () => void;

    /** Porta l'anteprima al punto del cursore. */
    vaiNelPdf?: () => void;

    /**
     * Perché «Vai qui nel PDF» non si può fare adesso, se non si può.
     *
     * Si chiede al momento dell'apertura del menù, non alla creazione
     * dell'estensione: lo stato dell'anteprima cambia mentre scrivi, e una
     * risposta congelata direbbe il falso quasi sempre.
     */
    perchePdfNo?: () => string | undefined;
}


interface Direttiva
{
    chiave: string;
    valore: string;
    daVirgolette: boolean;
    da: number;        // inizio del VALORE nel documento
    a: number;         // fine del valore
    riga: number;
    inizioRiga: number;
}


// Le posizioni coperte da un literal inline. Dentro i backtick un META è un
// ESEMPIO — il documento d'esempio ne cita uno nella tabella della sintassi —
// e trattarlo da direttiva prometterebbe una modifica su un testo che nessuno
// comporrà mai. È lo stesso recinto che il preprocessore ha già
// (mapOutsideInlineCode): qui la regola si ripete perché i due mondi non
// condividono codice, e la duplicazione è dichiarata invece che dimenticata.
function dentroLiteral(riga: string): (i: number) => boolean
{
    const coperto = new Set<number>();
    for (const m of riga.matchAll(/`+[^`]*`+/g))
    {
        for (let i = 0; i < m[0].length; i++) { coperto.add(m.index + i); }
    }
    return (i) => coperto.has(i);
}


function direttive(view: EditorView): Direttiva[]
{
    const trovate: Direttiva[] = [];
    let recinto: string | null = null;

    for (let n = 1; n <= view.state.doc.lines; n++)
    {
        const riga = view.state.doc.line(n);

        const apertura = FENCE.exec(riga.text);
        if (apertura)
        {
            recinto = recinto === null ? (apertura[1] ?? "```") : null;
            continue;
        }
        if (recinto !== null) { continue; }

        const literal = dentroLiteral(riga.text);
        META.lastIndex = 0;

        for (const m of riga.text.matchAll(META))
        {
            if (literal(m.index)) { continue; }

            const virgolettato = m[2] !== undefined;
            const valore = m[2] ?? m[3] ?? "";
            const dentro = riga.text.indexOf(valore, m.index);

            trovate.push({
                chiave: m[1] ?? "",
                valore,
                daVirgolette: virgolettato,
                da: riga.from + dentro,
                a: riga.from + dentro + valore.length,
                riga: n,
                inizioRiga: riga.from,
            });
        }
    }

    return trovate;
}


const apriCampo = StateEffect.define<Direttiva | null>();


// La riga illuminata. È una decorazione di RIGA, non di testo: si accende
// tutta la riga anche dove non c'è carattere, così si distingue con la coda
// dell'occhio mentre si scorre.
const illuminaMeta = ViewPlugin.fromClass(class
{
    decorations: DecorationSet;

    constructor(view: EditorView) { this.decorations = this.calcola(view); }

    update(u: ViewUpdate): void
    {
        if (u.docChanged || u.viewportChanged) { this.decorations = this.calcola(u.view); }
    }

    calcola(view: EditorView): DecorationSet
    {
        const viste = new Set<number>();
        const marchi: Range<Decoration>[] = [];

        for (const d of direttive(view))
        {
            // Due direttive sulla stessa riga sono una riga sola: due
            // decorazioni identiche sulla stessa posizione fanno lanciare
            // CodeMirror.
            if (viste.has(d.inizioRiga)) { continue; }
            viste.add(d.inizioRiga);
        }

        return Decoration.set(marchi, true);
    }
}, { decorations: (v) => v.decorations });


const campo = StateField.define<Tooltip | null>({
    create: () => null,

    update(prima, tr)
    {
        // Una modifica altrove sposta le posizioni che il campo sta puntando:
        // si chiude, invece di scrivere nel posto sbagliato.
        let dopo = tr.docChanged ? null : prima;
        for (const e of tr.effects)
        {
            if (e.is(apriCampo)) { dopo = e.value === null ? null : tooltipPer(e.value); }
        }
        return dopo;
    },

    provide: (f) => showTooltip.from(f),
});


function tooltipPer(d: Direttiva): Tooltip
{
    return {
        pos: d.da,
        above: false,
        arrow: true,
        create: (view) =>
        {
            const dom = document.createElement("div");
            dom.className = "cm-meta-tooltip";

            const etichetta = document.createElement("label");
            etichetta.textContent = d.chiave;

            const input = document.createElement("input");
            input.value = d.valore;
            input.spellcheck = false;

            const chiudi = (): void =>
            {
                view.dispatch({ effects: apriCampo.of(null) });
                view.focus();
            };

            const applica = (): void =>
            {
                const nuovo = input.value;

                // Le virgolette le decide il VALORE, non chi scrive: uno
                // spazio senza virgolette spezzerebbe la direttiva, e il
                // documento smetterebbe di comporre per un dettaglio di
                // sintassi che nessuno ha scelto.
                const serve = d.daVirgolette || /[\s"?]/.test(nuovo);
                const testo = serve ? `"${nuovo.replace(/"/g, "'")}"` : nuovo;
                const da = d.daVirgolette ? d.da - 1 : d.da;
                const a = d.daVirgolette ? d.a + 1 : d.a;

                view.dispatch({
                    changes: { from: da, to: a, insert: testo },
                    effects: apriCampo.of(null),
                });
                view.focus();
            };

            const ok = document.createElement("button");
            ok.type = "button";
            ok.className = "primario";
            ok.textContent = "Applica";
            ok.addEventListener("mousedown", (e) => { e.preventDefault(); applica(); });

            const annulla = document.createElement("button");
            annulla.type = "button";
            annulla.textContent = "Annulla";
            annulla.addEventListener("mousedown", (e) => { e.preventDefault(); chiudi(); });

            input.addEventListener("keydown", (e) =>
            {
                if (e.key === "Enter") { e.preventDefault(); applica(); }
                if (e.key === "Escape") { e.preventDefault(); chiudi(); }
            });

            dom.append(etichetta, input, ok, annulla);
            return { dom, mount: () => { input.focus(); input.select(); } };
        },
    };
}


interface Voce
{
    testo: string;
    scorciatoia?: string;
    spenta?: boolean;
    /** Perché è spenta: si dice, invece di lasciarlo indovinare. */
    perche?: string;
    azione: () => void;
}


function menu(view: EditorView, x: number, y: number, voci: Voce[]): void
{
    const dom = document.createElement("div");
    dom.className = "cm-menu";
    dom.setAttribute("role", "menu");

    const chiudi = (): void =>
    {
        dom.remove();
        window.removeEventListener("mousedown", fuori, true);
        window.removeEventListener("keydown", tasto, true);
        window.removeEventListener("resize", chiudi);
    };

    const fuori = (e: MouseEvent): void => { if (!dom.contains(e.target as Node)) { chiudi(); } };
    const tasto = (e: KeyboardEvent): void => { if (e.key === "Escape") { chiudi(); view.focus(); } };

    for (const v of voci)
    {
        const b = document.createElement("button");
        b.type = "button";
        b.setAttribute("role", "menuitem");
        b.append(document.createTextNode(v.testo));

        if (v.scorciatoia !== undefined)
        {
            const s = document.createElement("span");
            s.className = "cm-menu-scorciatoia";
            s.textContent = v.scorciatoia;
            b.append(s);
        }

        if (v.spenta === true)
        {
            b.disabled = true;
            if (v.perche !== undefined) { b.title = v.perche; }
        }
        else
        {
            // Il clic sul menu NON deve togliere il fuoco all'editor: taglia e
            // copia lavorano sulla selezione del DOM, e su un bottone a fuoco
            // non c'e' niente da tagliare. Misurato: senza questo, «Taglia»
            // chiudeva il menu e lasciava il testo dov'era.
            b.addEventListener("mousedown", (e) => e.preventDefault());
            b.addEventListener("click", () => { chiudi(); view.focus(); v.azione(); });
        }
        dom.append(b);
    }

    document.body.append(dom);

    // Il menù non deve uscire dalla finestra: si misura DOPO averlo attaccato,
    // perché prima non ha dimensioni.
    const r = dom.getBoundingClientRect();
    dom.style.left = `${Math.min(x, window.innerWidth - r.width - 8)}px`;
    dom.style.top = `${Math.min(y, window.innerHeight - r.height - 8)}px`;

    window.addEventListener("mousedown", fuori, true);
    window.addEventListener("keydown", tasto, true);
    window.addEventListener("resize", chiudi);
}


function menuContestuale(opzioni: Opzioni): Extension
{
    return EditorView.domEventHandlers({
        contextmenu(e, view)
        {
            const pos = view.posAtCoords({ x: e.clientX, y: e.clientY });
            if (pos === null) { return false; }

            const riga = view.state.doc.lineAt(pos).number;
            const qui = direttive(view).filter((d) => d.riga === riga);

            const selezione = view.state.selection.main;
            const conTesto = !selezione.empty;

            const voci: Voce[] = qui.map((d) => ({
                testo: `Modifica «${d.chiave}»…`,
                azione: () => view.dispatch({ effects: apriCampo.of(d) }),
            }));

            if (conTesto)
            {
                // Con del testo selezionato il menu offre TUTTE le marcature, e
                // ognuna dice se la mette o la toglie: e' la stessa domanda che
                // ti stai facendo guardando il testo.
                for (const quale of MARCATURE)
                {
                    const gia = marcaturaDellaSelezione(view.state, quale);

                    voci.push({
                        testo: `${gia === null ? "Rendi" : "Togli"} ${quale}`,
                        azione: () => { if (gia === null) { marca(view, quale); } else { togli(view, gia); } },
                    });
                }
            }
            else
            {
                // Senza selezione si parla solo di quello che c'e' sotto il
                // puntatore: il punto in cui hai cliccato dice gia' di quale
                // grassetto stiamo parlando.
                for (const trovata of marcatureIn(view.state, pos))
                {
                    voci.push({
                        testo: `Togli ${trovata.quale}`,
                        azione: () => togli(view, trovata),
                    });
                }
            }

            // Taglia e copia solo se c'e' qualcosa da tagliare o copiare: una
            // voce che c'e' sempre e a volte non fa niente insegna a non
            // fidarsi del menu.
            if (conTesto)
            {
                voci.push({
                    testo: "Taglia",
                    scorciatoia: "⌃X",
                    azione: () => { document.execCommand("cut"); view.focus(); },
                });
                voci.push({
                    testo: "Copia",
                    scorciatoia: "⌃C",
                    azione: () => { document.execCommand("copy"); view.focus(); },
                });
            }

            // Incolla: il browser lo concede al programma solo in secure
            // context (https o localhost). Altrove la voce c'e' ma e' SPENTA e
            // dice perche' — meglio di una voce viva che non fa niente.
            const puoIncollare = window.isSecureContext && navigator.clipboard !== undefined;
            voci.push({
                testo: "Incolla",
                scorciatoia: "⌃V",
                spenta: !puoIncollare,
                perche: puoIncollare ? undefined : "il browser lo concede solo su https o localhost: usa ⌃V",
                azione: () =>
                {
                    void navigator.clipboard.readText().then((testo) =>
                    {
                        view.dispatch(view.state.replaceSelection(testo));
                        view.focus();
                    });
                },
            });

            if (opzioni.vaiNelPdf !== undefined)
            {
                const perche = opzioni.perchePdfNo?.();

                voci.push({
                    testo: "Vai qui nel PDF",
                    spenta: perche !== undefined,
                    perche,
                    azione: opzioni.vaiNelPdf,
                });
            }

            if (opzioni.componi !== undefined)
            {
                voci.push({ testo: "Componi", scorciatoia: "⌃↵", azione: opzioni.componi });
            }

            e.preventDefault();
            menu(view, e.clientX, e.clientY, voci);
            return true;
        },
    });
}


const stile = EditorView.baseTheme({
    // Le righe META non si colorano: il giallo c'era per farle notare, ma una
    // direttiva la si riconosce dalla forma (`<? META:... ?>`) e un colore su
    // ogni riga di intestazione e' rumore su un documento che si legge tutto.
    // Restano il menu contestuale e il tooltip: quelli servono quando la vuoi
    // CAMBIARE, e li chiedi tu.
    ".cm-meta-tooltip": {
        display: "flex",
        alignItems: "center",
        gap: "6px",
        padding: "7px 9px",
        border: "1px solid var(--linea)",
        borderRadius: "7px",
        background: "var(--pannello)",
    },
    ".cm-meta-tooltip label": { fontSize: "12px", color: "var(--fioco)", fontFamily: "var(--mono)" },
    ".cm-meta-tooltip input": {
        font: "inherit",
        fontSize: "13px",
        padding: "4px 7px",
        minWidth: "22ch",
        border: "1px solid var(--linea)",
        borderRadius: "5px",
        background: "var(--carta)",
        color: "var(--inchiostro)",
    },
});


export function editorMenu(opzioni: Opzioni = {}): Extension
{
    return [illuminaMeta, campo, menuContestuale(opzioni), stile];
}
